/**
 * Inline widget placement (#592) — the ONE typed contract for HOW the widget
 * behaves inside the blessed div (`div.bhai-<customerId>-chat`):
 *
 *   "open"   — the chat renders always-open in the container (the shipped
 *              pre-#592 inline behavior; the default).
 *   "expand" — the container starts as the skinned living-bar surface (real
 *              typeable input, typewriter invite lines, nudge pills) and
 *              expands in place to the full conversation on interaction.
 *   "agent"  — a site-agent field: the composer alone, centred in the
 *              container with the page's questions above it and nothing else.
 *              Commits on ENTER rather than expanding on focus, and the
 *              conversation opens with the composer at its foot, so the field
 *              reads as having dropped to the bottom.
 *   "nudges" — questions only: the page's nudge questions as a group of
 *              pills and nothing else — no field, no bar. Made for the foot
 *              of an article. A pill opens the conversation in place with
 *              that question already asked, exactly as an expand bar does.
 *              It wears the same look as the lower bar's question pills
 *              (`nudgesSurfaceLook`), and a page with no nudges paints
 *              nothing at all.
 *
 * `agent` is a MODE, not a living-bar skin, because it is not a bar and there
 * is no bar it could be: it has no viewport-pinned form, so offering it in the
 * Lower bar picker would have been offering something that does not exist.
 *
 * ROLLOUT ORDERING: an older bh.js narrows the wire value against its own copy
 * of this list and falls back to DEFAULT_INLINE_MODE — which is "open", a full
 * panel, not a quieter shape. So `agent` is safe as a per-DIV attribute at any
 * time (an unrecognised attribute falls back to the tenant mode) but should
 * only become a TENANT default once the fleet carries the tag that ships it.
 * `nudges` is the opposite case: on an older bh.js the attribute is unknown and
 * the div falls back to the tenant mode, which is a full panel by default — so
 * a `nudges` div belongs on a page only once that tenant runs the tag that
 * ships it.
 *
 * Resolution order: a valid `data-bhai-inline` attribute on the blessed div
 * wins over the tenant setting — the site designer placed the div and knows
 * what that spot needs.
 *
 * Pure module (no zod) so the widget snippet (bh.js) can import it safely —
 * the bundle-integrity gate in widget/build.ts bans zod there. Zod schemas
 * live in ./inline-schema.ts for dashboard / API.
 */
import type { LivingBarSkin } from "./living-bar";

export const WIDGET_INLINE_KEY = "widget_inline";

/** The phone line (2026-08-26): below this viewport width the "Show on
 *  phones" setting may hide every surface, and chat.css runs its compact
 *  treatments (@media and @container both use the literal 640px — CSS cannot
 *  read this constant, so a change here must change those blocks too). */
export const PHONE_BREAKPOINT_PX = 640;

export const INLINE_MODES = ["expand", "open", "agent", "nudges"] as const;
export type InlineMode = (typeof INLINE_MODES)[number];

/**
 * `data-bhai-chips="off"` on the blessed div — suppress the rotating question
 * chip above an agent field.
 *
 * Per-DIV rather than a tenant setting, because it is a layout decision about
 * ONE spot: a hero that already lists the same questions, or a container tight
 * enough that a second line of text crowds the field. Opt-OUT, so the chip
 * shows unless a designer says otherwise.
 */
export function inlineChipsAllowed(attr: string | null | undefined): boolean {
  return (attr ?? "").trim().toLowerCase() !== "off";
}

/**
 * The embed example the dashboard shows for a container — DERIVED from the
 * mode it describes, never hardcoded. Until 2026-08-26 the dashboard always
 * printed the agent block whatever the tenant had selected; a developer
 * following it pasted the wrong widget (that is how Lynda's rail shipped
 * `expand` into a 340px rail). Sizing guidance is per-mode because the modes
 * need different things: open fills a height, expand needs only width (height
 * arrives on expand), agent centres in the height you give it.
 */
export function inlineEmbedExample(customerId: string, mode: InlineMode): string {
  const cls = `bhai-${customerId}-chat`;
  switch (mode) {
    case "open":
      return `<div class="${cls}"\n     data-bhai-inline="open"\n     style="width:100%;height:480px"></div>`;
    case "expand":
      return `<div class="${cls}"\n     data-bhai-inline="expand"\n     style="width:100%"></div>`;
    case "agent":
      return `<div class="${cls}"\n     data-bhai-inline="agent"\n     data-bhai-chips="off"\n     style="width:100%;height:340px"></div>`;
    case "nudges":
      return `<div class="${cls}"\n     data-bhai-inline="nudges"\n     style="width:100%"></div>`;
  }
}

/**
 * When the LOWER BAR (the floating bottom bar) is allowed to appear,
 * relative to the customer's own inline containers:
 *
 *   "always"        — the bar shows on every in-scope page, including pages
 *                     that already carry an inline container.
 *   "unless_inline" — a page with an inline container gets the inline unit
 *                     ONLY; pages without one still get the bar. This is the
 *                     shipped behaviour, so it is the default.
 *   "inline_only"   — the bar never appears; the widget shows up only where
 *                     the customer placed a container.
 */
export const BAR_MODES = ["always", "unless_inline", "inline_only"] as const;
export type BarMode = (typeof BAR_MODES)[number];

/** Matches what every install does today — changing it would move the widget
 *  on live sites without anyone asking. */
export const DEFAULT_BAR_MODE: BarMode = "unless_inline";

export function isBarMode(v: unknown): v is BarMode {
  return typeof v === "string" && (BAR_MODES as readonly string[]).includes(v);
}

/**
 * Does the lower bar render on THIS page? `pageHasInline` is whether the page
 * carries a blessed div. Pure so the snippet and its tests share one answer.
 */
export function lowerBarAllowed(mode: BarMode, pageHasInline: boolean): boolean {
  if (mode === "inline_only") return false;
  if (mode === "always") return true;
  return !pageHasInline;
}

/**
 * What the LOWER BAR shows:
 *
 *   "full"   — the bar: its question pills above a capsule the visitor can
 *              click or talk to. The shipped bar, so it is the default.
 *   "nudges" — the question pills alone, with no capsule. A pill opens the
 *              chat with that question asked; a page with no nudges shows no
 *              bar. Older bundles do not read the field and keep the full bar.
 */
export const BAR_CONTENTS = ["full", "nudges"] as const;
export type BarContent = (typeof BAR_CONTENTS)[number];
export const DEFAULT_BAR_CONTENT: BarContent = "full";

/** Short names for what the lower bar shows — the dashboard's one copy. */
export const BAR_CONTENT_LABELS: { [C in BarContent]: string } = {
  full: "The bar and its questions",
  nudges: "Questions only",
};

export function isBarContent(v: unknown): v is BarContent {
  return typeof v === "string" && (BAR_CONTENTS as readonly string[]).includes(v);
}

/** Most pills a questions-only inline container shows: the whole rotation
 *  window (2 this-page + 2 also-worth-asking). It is in-flow and wraps, so it
 *  never needs the phone budget the floating bar does. */
export const NUDGES_INLINE_MAX = 4;

/** "open" preserves every shipped inline install (Brian, 2026-08-13). */
export const DEFAULT_INLINE_MODE: InlineMode = "open";

/** Short names for each inline style — the dashboard's one copy (the Inline
 *  page's list and the A/B test editor both read it). Keyed by the mode, so a
 *  new mode cannot ship without one. */
export const INLINE_MODE_LABELS: { [M in InlineMode]: string } = {
  open: "Chat always open",
  expand: "Bar, expands in place",
  agent: "Site agent",
  nudges: "Questions only",
};

export function isInlineMode(v: unknown): v is InlineMode {
  return typeof v === "string" && (INLINE_MODES as readonly string[]).includes(v);
}

/** Effective mode for one blessed div: valid div attribute beats the tenant
 *  setting; a missing or garbage attribute falls back to the tenant mode. */
export function inlineModeFor(tenantMode: InlineMode, divAttr: string | null): InlineMode {
  return isInlineMode(divAttr) ? divAttr : tenantMode;
}

/**
 * Inline degradation for viewport-pinned skins. The side rails are FIXED
 * right-edge chrome by design — meaningless inside an in-flow container —
 * so an inline surface maps each to its nearest in-flow sibling and leaves
 * every other skin (glass-capsule family included) untouched. Lives here,
 * not in bubble.tsx, so the render stays free of member equality checks
 * (the glass-capsule family test bans them there).
 */
export function inlineSurfaceSkin(skin: LivingBarSkin | null): LivingBarSkin | null {
  switch (skin) {
    case "side_rail":
      return "on_this_page";
    case "side_theater":
      return "split_theater";
    default:
      return skin;
  }
}
